import { endpoints } from '@api'
import { Box, Image, useDisclosure, useToast } from '@chakra-ui/react'
import JoinWaitlistButton from '@handlers/sessions/components/join-waitlist'
import { VideoModal } from '@handlers/signup-v2/components/video-modal'
import { useExperienceManager } from '@hooks'
import { useStoreState } from 'easy-peasy'
import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useHistory, useLocation } from 'react-router-dom'
import type { StoreModel } from 'src/store/types'
import { useClinicControllerGetPromotionDiscount } from '~/clinician-api'
import { EhrWaitlistBanner } from './ehr-waitlist-banner'
import PromotionDiscountInfoBanner from './promotion-discount-info-banner'
import { ReferralBanner } from './referral-banner'
import { WelcomeAndSurveyBanner } from './welcome-banner'

interface InfoBannerProps {
  hasLessThanNumberRecordedSessions: boolean
}

const WelcomeVideoUrl =
  'https://res.cloudinary.com/hellojoy/video/upload/v1742925423/onboarding/Blueprint-Onboarding_with_Vivian-compile_compressed.mp4'

const InfoBanner = ({ hasLessThanNumberRecordedSessions }: InfoBannerProps) => {
  const [isWelcomeBannerVisible, setIsWelcomeBannerVisible] = useState(true)
  const [isForceEhrBannerVisible, setIsForceEhrBannerVisible] = useState(false)
  const [isEhrBannerVisible, setIsEhrBannerVisible] = useState(false)
  const [
    isPromotionDiscountBannerVisible,
    setIsPromotionDiscountBannerVisible
  ] = useState(false)
  const referralDismissedAtKey = 'referralBannerDismissedAt'

  const [isReferralBannerVisible, setIsReferralBannerVisible] = useState(() => {
    const dismissedAt = localStorage.getItem(referralDismissedAtKey)
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10)
      const oneMonthInMs = 30 * 24 * 60 * 60 * 1000
      const elapsedTime = Date.now() - dismissedTime
      if (elapsedTime < oneMonthInMs) {
        return false
      }
    }
    return true
  })

  const user = useStoreState((state: StoreModel) => state.auth.user)
  const queryClient = useQueryClient()
  const toast = useToast()
  const history = useHistory()

  const { search } = useLocation()
  const urlParams = new URLSearchParams(search)
  const playEhrVideo = urlParams.get('play_ehr_video') === 'true'
  const joinEhrWaitlist = urlParams.get('join_ehr_waitlist') === 'true'

  const {
    isDocumentationAutomationFreePlan,
    isDocumentationAutomationOnly,
    isDocumentationAutomationEnterprise,
    showReferralButton,
    isEhrWaitlistEnabled,
    usageBasedPricingEnabled
  } = useExperienceManager()

  const isSelfServe =
    isDocumentationAutomationOnly && !isDocumentationAutomationEnterprise

  const { data: featureWaitlists, isLoading: isWaitlistLoading } = useQuery<
    string[]
  >(
    [endpoints.getFeatureWaitlist.getCacheId(), 'ehrBanner'],
    () => endpoints.getFeatureWaitlist.request({}) as Promise<string[]>
  )

  const {
    isOpen: isOpenPreviewVideo,
    onOpen: onOpenPreviewVideo,
    onClose: onClosePreviewVideo
  } = useDisclosure()

  const {
    isOpen: isOpenPreviewEhrVideo,
    onOpen: onOpenPreviewEhrVideo,
    onClose: onClosePreviewEhrVideo
  } = useDisclosure()

  const { mutateAsync: joinWaitlist } = useMutation(
    endpoints.postJoinWaitlist.request,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(endpoints.getFeatureWaitlist.getCacheId())
      }
    }
  )

  const { data: promotionDiscount } = useClinicControllerGetPromotionDiscount(
    user?.clinic_id ?? ''
  )

  useEffect(() => {
    if (
      !isWaitlistLoading &&
      !featureWaitlists?.includes('ehr') &&
      !localStorage.getItem(ehrDismissedKey)
    ) {
      setIsEhrBannerVisible(true)
    }
  }, [isWaitlistLoading, featureWaitlists])

  useEffect(() => {
    if (
      promotionDiscount &&
      !localStorage.getItem(promotionDiscountDismissedKey)
    ) {
      setIsPromotionDiscountBannerVisible(true)
    }
  }, [promotionDiscount])

  useEffect(() => {
    if (joinEhrWaitlist) {
      setIsForceEhrBannerVisible(true)

      joinWaitlist(
        { feature: 'ehr' },
        {
          onSuccess: () => {
            toast({
              title: 'Success',
              description: 'Early access has been requested!',
              status: 'success'
            })
            window.history.pushState({}, '', window.location.pathname)
          }
        }
      )
    }
  }, [joinEhrWaitlist, joinWaitlist, queryClient, toast, history])

  useEffect(() => {
    if (playEhrVideo) {
      setIsForceEhrBannerVisible(true)
      onOpenPreviewEhrVideo()
      window.history.pushState({}, '', window.location.pathname)
    }
  }, [playEhrVideo, onOpenPreviewEhrVideo])

  const localStorageDismissedKey = 'welcomeBannerDismissed'
  const localStorageDismissedAtKey = 'welcomeBannerDismissedAt'

  const referralDismissedKey = 'referralBannerDismissed'
  const ehrDismissedKey = 'ehrBannerDismissed'
  const promotionDiscountDismissedKey = 'promotionDiscountBannerDismissed'
  const showReferralBanner =
    showReferralButton &&
    !isDocumentationAutomationFreePlan &&
    isReferralBannerVisible

  const handleWelcomeClose = () => {
    localStorage.setItem(localStorageDismissedKey, 'true')
    localStorage.setItem(localStorageDismissedAtKey, Date.now().toString())
    setIsWelcomeBannerVisible(false)
  }

  const handleReferralClose = () => {
    localStorage.setItem(referralDismissedKey, 'true')
    localStorage.setItem(referralDismissedAtKey, Date.now().toString())
    setIsReferralBannerVisible(false)
  }

  const handleEhrClose = () => {
    localStorage.setItem(ehrDismissedKey, 'true')
    setIsEhrBannerVisible(false)
    window.history.pushState({}, '', window.location.pathname)
  }

  const handlePromotionDiscountClose = () => {
    localStorage.setItem(promotionDiscountDismissedKey, 'true')
    setIsPromotionDiscountBannerVisible(false)
  }

  const renderBanner = () => {
    if (isPromotionDiscountBannerVisible && !usageBasedPricingEnabled) {
      return (
        <PromotionDiscountInfoBanner
          promotionDiscount={promotionDiscount!}
          onClose={handlePromotionDiscountClose}
        />
      )
    }

    if (
      (isSelfServe && isEhrWaitlistEnabled && isEhrBannerVisible) ||
      isForceEhrBannerVisible
    ) {
      return (
        <EhrWaitlistBanner
          onClose={handleEhrClose}
          onPlayVideo={onOpenPreviewEhrVideo}
        />
      )
    }

    if (isWelcomeBannerVisible && hasLessThanNumberRecordedSessions) {
      return (
        <WelcomeAndSurveyBanner
          user={user}
          handleClose={handleWelcomeClose}
          setIsVisible={setIsWelcomeBannerVisible}
          localStorageDismissedAtKey={localStorageDismissedAtKey}
          localStorageDismissedKey={localStorageDismissedKey}
          onOpenPreviewVideo={onOpenPreviewVideo}
        />
      )
    }

    if (showReferralBanner) {
      return <ReferralBanner handleClose={handleReferralClose} />
    }

    return null
  }

  if (!user || isWaitlistLoading) return null

  return (
    <Box position="relative">
      {renderBanner()}
      <VideoModal
        url={WelcomeVideoUrl}
        isOpenPreviewVideo={isOpenPreviewVideo}
        onClosePreviewVideo={onClosePreviewVideo}
      />
      <VideoModal
        wistiaId="7gn2kiey5m"
        isOpenPreviewVideo={isOpenPreviewEhrVideo}
        onClosePreviewVideo={onClosePreviewEhrVideo}
        footerContent={
          <Box
            py={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <JoinWaitlistButton
              feature="ehr"
              buttonText="Join the waitlist"
              width="180px"
              fontSize="16px"
              variant="solid"
            />
          </Box>
        }
      />
    </Box>
  )
}

export default InfoBanner
